import { useEffect, useMemo, useState } from 'react';
import { createContainer } from 'unstated-next';
import { useApi, Res, isTransient, reportTemporaryFailure } from './api';
import { ApiCategory } from '../types';
import { useLocation } from 'react-router-dom';

/* eslint-disable react-hooks/rules-of-hooks */

export interface TreeNode {
	id: number;
	parentId: number;
	perma: string;
	name: string;
	children: TreeNode[];
	hide: boolean;
	attributes?: string[];
}

function rootNode(children: TreeNode[]): TreeNode {
	return {
		id: 0,
		parentId: 0,
		perma: '/',
		name: 'root',
		children,
		hide: false,
	};
}
function buildTree(data: undefined | Record<string, ApiCategory>): {
	tree: TreeNode;
	permaIndexedCategories: Record<string, TreeNode>;
} {
	if (data === undefined) return { tree: rootNode([]), permaIndexedCategories: {} };
	const nodes: Record<string, TreeNode> = {};
	const permaIndexedCategories: Record<string, TreeNode> = {};

	for (let key in data) {
		const category = data[key];
		const node: TreeNode = {
			id: category.id,
			parentId: category.parentId ?? 0,
			perma: category.perma,
			name: category.name,
			children: [],
			hide: category.hide ? category.hide : false,
			attributes: category.attributes ?? undefined,
		};
		nodes[category.id] = node;
		permaIndexedCategories[category.perma] = node;
	}
	const result: TreeNode[] = [];
	for (let key in nodes) {
		var item = nodes[key];
		if (item.parentId) {
			if (nodes[item.parentId]) {
				nodes[item.parentId].children = nodes[item.parentId].children || [];
				nodes[item.parentId].children.push(item);
			}
		} else {
			result.push(item);
		}
	}
	return {
		tree: rootNode(result),
		permaIndexedCategories,
	};
}

function buildPathToPerma(node: TreeNode, perma: string): TreeNode[] | null {
	if (node.perma === perma) return [node];
	for (var child of node.children) {
		const subpath = buildPathToPerma(child, perma);
		if (subpath) return node.name === 'root' ? subpath : [node, ...subpath];
	}
	return null;
}

function buildCategoryTree() {
	const { data, error } = useApi(Res.categories, {});
	const isDataLoaded = !!data;
	// eslint-disable-next-line react-hooks/exhaustive-deps
	const { tree, permaIndexedCategories } = useMemo(() => buildTree(data), [isDataLoaded]);
	const [openedNodes, setOpenedNodes] = useState<number[]>([]);
	const [dirtyTree, setDirtyTree] = useState(false);
	const location = useLocation();

	/*
	 * Counted, not yet acted on. buildTree answers with an empty root when there is no data, so a
	 * category list that merely failed to arrive becomes a shop with no categories: empty
	 * navigation, empty breadcrumbs, and a not-found page on every super-category url, whose
	 * check is `if (!subtree)`.
	 */
	useEffect(() => {
		if (data === undefined && isTransient(error)) {
			reportTemporaryFailure(
				{
					url: error.url,
					status: error.status,
					kind: 'read-as-answer',
					resource: 'categories',
				},
				error
			);
		}
	}, [error, data]);

	function isOpenByPerma(node: TreeNode, perma: string): boolean {
		return node.perma === perma || node.children.some((e) => isOpenByPerma(e, perma));
	}

	function getCategoryById(id: number): ApiCategory | undefined {
		return data?.[id];
	}

	useEffect(() => {
		const perma = location.pathname.slice(1);
		let isSuperCategoryPage = false;
		let i = 0;
		while (i < tree.children.length) {
			if (tree.children[i].perma === perma) {
				isSuperCategoryPage = true;
				break;
			}
			i++;
		}

		const isCategoryPage = /-c-/g.test(perma) || /^c\//g.test(perma);
		const isProductPerma = /-p-/g.test(perma);
		if ((isCategoryPage && !isSuperCategoryPage) || openedNodes.length > 0 || isProductPerma) {
			setDirtyTree(true);
		} else {
			setDirtyTree(false);
		}
	});

	return {
		tree,
		permaIndexedCategories,
		openedNodes,
		dirtyTree,
		setDirtyTree,
		getCategoryById,
		getName(perma: string): string {
			return Object.values(data ?? {}).find((e) => e.perma === perma)?.name ?? '';
		},
		getSubcategories(perma: string): ApiCategory[] {
			const parent = Object.values(data ?? {}).find((e) => e.perma === perma);
			return Object.values(data ?? {}).filter((e) => e.parentId === parent?.id);
		},
		getBreadcrumbs(perma: string): TreeNode[] {
			return buildPathToPerma(tree, perma) ?? [];
		},
		getSupercategoryId(perma: string): number {
			const categoryId = Object.values(data ?? {}).find((e) => e.perma === perma)?.id ?? 0;
			let currentCategory = getCategoryById(categoryId);
			if (currentCategory !== undefined) {
				// return the tree node before the root node (root node's parentId is 0)
				while (currentCategory.parentId !== 0) {
					if (currentCategory.parentId) {
						currentCategory = getCategoryById(currentCategory.parentId) as ApiCategory;
					}
				}
				return currentCategory.id as number;
			}

			return 0;
		},
		getMainCategoryId(categoryId: number): number {
			let category = getCategoryById(categoryId);
			if (category !== undefined) {
				while (category.parentId !== 0) {
					if (category.parentId) {
						category = getCategoryById(category.parentId) as ApiCategory;
					}
				}
				return category.id as number;
			}
			return 0;
		},
		toggleNode(node: TreeNode) {
			setOpenedNodes((list) => {
				if (list.includes(node.id)) return list.filter((e) => e !== node.id);
				else return [...list, node.id];
			});
		},
		isOpen(node: TreeNode, perma: string) {
			return isOpenByPerma(node, perma) || openedNodes.includes(node.id);
		},
	};
}

export const CategoryTreeContainer = createContainer(buildCategoryTree);
